import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import { LitraMark } from "@/components/Brand";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Today from "@/pages/Today";
import Dashboard from "@/pages/Dashboard";
import Decode from "@/pages/Decode";
import Doctrine from "@/pages/Doctrine";
import Path from "@/pages/Path";
import Universe from "@/pages/Universe";
import Create from "@/pages/Create";
import Vault from "@/pages/Vault";
import Gallery from "@/pages/Gallery";
import Dictionary from "@/pages/Dictionary";
import Challenges from "@/pages/Challenges";
import Pricing from "@/pages/Pricing";
import Profile from "@/pages/Profile";
import PublicProfile from "@/pages/PublicProfile";
import Admin from "@/pages/Admin";
import PaymentReturn from "@/pages/PaymentReturn";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0B0C0E]">
    <div className="animate-pulse flex flex-col items-center gap-3"><LitraMark size={40} /><span className="text-[var(--bone-faint)] text-sm">Loading Litrametry…</span></div>
  </div>
);

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return (<AppShell><Outlet /></AppShell>);
}

function OnboardingGate() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded) return <Navigate to="/today" replace />;
  return <Onboarding />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/payment-return" element={<PaymentReturn />} />
      <Route path="/u/:username" element={<PublicProfile />} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route element={<ProtectedLayout />}>
        <Route path="/today" element={<Today />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/decode" element={<Decode />} />
        <Route path="/doctrine" element={<Doctrine />} />
        <Route path="/path" element={<Path />} />
        <Route path="/life" element={<Navigate to="/path" replace />} />
        <Route path="/universe" element={<Universe />} />
        <Route path="/archive" element={<Navigate to="/universe" replace />} />
        <Route path="/studio" element={<Create />} />
        <Route path="/create" element={<Navigate to="/studio" replace />} />
        <Route path="/vault" element={<Vault />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/dictionary" element={<Dictionary />} />
        <Route path="/challenges" element={<Challenges />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" theme="dark" />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
